import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { CHAT_DB_SERVER_URL } from '../../config/backend-endpoints';
import { ensureUtcIso, formatIstDate, formatIstDateTime } from '../../utils/dateTime';
import DialogModal from '../../components/Modal/DialogModal';

const CHAT_WORKSPACE_URL = import.meta.env.VITE_CHAT_DOMAIN_URL || 'https://chat.paralegal.lk';
const PRICING_PATH = '/professional-plan';
const BASE_PLAN_NAME = 'Paralegal Professional Plan';
const PAYMENT_METHOD_RETURN_MESSAGE =
  'Payment method setup may take a moment to confirm. Refresh if it does not appear immediately.';

const formatCardLabel = (method) => {
  const brand = String(method?.card_brand || method?.method || 'Card').trim();
  const masked = String(method?.masked_card_no || '').trim();
  if (!masked) {
    return brand;
  }
  return `${brand} ${masked.replace(/\*/g, '•')}`;
};

const formatCardStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) {
    return 'Unknown';
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatPaymentMethodError = (value) => {
  const normalized = String(value || '').trim();
  if (normalized === 'replacement_primary_required') {
    return 'Add another card before removing the Primary card.';
  }
  if (normalized === 'replacement_required') {
    return 'Choose an existing card to replace before adding another saved card.';
  }
  if (normalized === 'secondary_card_required') {
    return 'Add a Secondary card before making the Primary card Secondary.';
  }
  if (normalized === 'primary_card_required') {
    return 'Add a Primary card before assigning a Secondary card.';
  }
  if (normalized === 'payment_method_not_active') {
    return 'This card is no longer active.';
  }
  return normalized || 'Unable to update payment method.';
};

const resolvePaymentMethodRole = (method) => {
  if (method?.is_primary) {
    return 'Primary';
  }
  if (method?.is_backup) {
    return 'Secondary';
  }
  return 'Saved';
};

const submitPayHerePreapprovalForm = ({ actionUrl, formPayload, method = 'POST' }) => {
  const form = document.createElement('form');
  form.method = String(method || 'POST').toUpperCase();
  form.action = actionUrl;
  form.style.display = 'none';

  Object.entries(formPayload || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = String(value);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

const TRIAL_STATUS_OPTIONS = new Set([
  'trialing',
  'converted_to_paid',
  'expired',
  'access_removal_started',
]);

const normalizeBillingInterval = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'annual' || normalized === 'yearly') {
    return 'yearly';
  }
  if (normalized === 'monthly') {
    return 'monthly';
  }
  if (normalized === 'trial') {
    return 'trial';
  }
  return normalized || null;
};

const formatDisplayLabel = (value) => {
  const normalized = normalizeBillingInterval(value);
  if (!normalized) {
    return '-';
  }
  if (normalized === 'yearly') {
    return 'Yearly';
  }
  if (normalized === 'monthly') {
    return 'Monthly';
  }
  if (normalized === 'trial') {
    return 'Trial';
  }
  return normalized
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatSubscriptionAmount = (amount, currency) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '-';
  }
  const currencyCode = String(currency || '').trim().toUpperCase();
  const formatted = new Intl.NumberFormat('en-LK', {
    maximumFractionDigits: 0,
  }).format(numeric);
  if (currencyCode === 'LKR') {
    return `Rs. ${formatted}`;
  }
  return currencyCode ? `${currencyCode} ${formatted}` : formatted;
};

const resolveBasePlanName = (planName) => {
  const normalized = String(planName || '').trim();
  if (!normalized) {
    return '-';
  }
  if (normalized.startsWith(BASE_PLAN_NAME)) {
    return BASE_PLAN_NAME;
  }
  return normalized;
};

const normalizeTrialStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized || !TRIAL_STATUS_OPTIONS.has(normalized)) {
    return null;
  }
  return normalized;
};

const resolveTrialDaysRemaining = (value, trialEndsAt) => {
  if (value !== null && value !== undefined && value !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.ceil(parsed));
    }
  }

  const normalized = String(trialEndsAt || '').trim();
  if (!normalized) {
    return null;
  }
  const trialEndsAtMs = Date.parse(ensureUtcIso(normalized));
  if (Number.isNaN(trialEndsAtMs)) {
    return null;
  }
  const diffMs = trialEndsAtMs - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

const deriveAccountModel = (entitlementPayload, subscriptionPayload) => {
  const entitlement = entitlementPayload || null;
  const subscription = subscriptionPayload?.subscription || null;
  const subscriptionEntitlement = subscriptionPayload?.entitlement || null;

  const entitlementChatAccess =
    typeof entitlement?.chat_access === 'boolean' ? entitlement.chat_access : null;
  const subscriptionChatAccess =
    typeof subscriptionEntitlement?.active === 'boolean' ? subscriptionEntitlement.active : null;
  const chatAccess = Boolean(
    entitlementChatAccess ?? subscriptionChatAccess ?? (subscription?.is_paid && subscription?.status === 'active')
  );
  const accessStatus = String(
    entitlement?.access_status ||
    entitlement?.status ||
    subscriptionEntitlement?.access_status ||
    subscriptionEntitlement?.status ||
    entitlement?.reason ||
    subscriptionEntitlement?.reason ||
    subscription?.status ||
    (chatAccess ? 'active' : 'inactive')
  )
    .trim()
    .toLowerCase();
  const billingStatus = String(
    entitlement?.billing_status ||
    subscriptionEntitlement?.billing_status ||
    ''
  )
    .trim()
    .toLowerCase();
  const entitlementReason = String(entitlement?.reason || subscriptionEntitlement?.reason || '').trim().toLowerCase();

  const hasPrepaidFutureSubscription = Boolean(subscriptionPayload?.has_prepaid_future_subscription || subscriptionPayload?.paid_now);
  const trialEndChargeScheduled = Boolean(subscriptionPayload?.trial_end_charge_scheduled) && !hasPrepaidFutureSubscription;
  const lastChargeStatus = String(subscriptionPayload?.last_charge_status || '').trim().toLowerCase() || null;
  const prepaidPlanCode = subscriptionPayload?.prepaid_plan_code || subscriptionPayload?.selected_plan_code || null;
  const prepaidStartAt = subscriptionPayload?.scheduled_start_at || null;
  const prepaidEndAt = subscriptionPayload?.scheduled_end_at || null;
  const prepaidAutoRenew = typeof subscriptionPayload?.auto_renew === 'boolean' ? subscriptionPayload.auto_renew : null;
  const trialStatus = normalizeTrialStatus(entitlement?.trial_status);
  const trialStartedAt = entitlement?.trial_started_at || null;
  const trialEndsAt = entitlement?.trial_ends_at || null;
  const trialDaysRemaining = resolveTrialDaysRemaining(entitlement?.trial_days_remaining, trialEndsAt);
  const trialEligible = Boolean(entitlement?.trial_eligible);
  const hasTrialContext = Boolean(trialStatus || trialStartedAt || trialEndsAt);
  const effectiveFrom = entitlement?.effective_from || subscriptionEntitlement?.effective_from || null;
  const effectiveTo = entitlement?.effective_to || subscriptionEntitlement?.effective_to || null;
  const hasAccessPeriod = Boolean(effectiveFrom || effectiveTo);
  const hasSubscription = Boolean(subscription) || chatAccess || hasAccessPeriod;
  const isPaid = (Boolean(subscription?.is_paid) && !(hasPrepaidFutureSubscription && trialStatus === 'trialing')) || (chatAccess && trialStatus !== 'trialing' && !hasTrialContext);
  const billingInterval = isPaid
    ? normalizeBillingInterval(subscription?.billing_interval) || null
    : hasTrialContext
      ? 'trial'
      : normalizeBillingInterval(subscription?.billing_interval);
  const planName = subscription?.plan_name
    ? resolveBasePlanName(subscription.plan_name)
    : hasSubscription || hasTrialContext || trialEligible
      ? BASE_PLAN_NAME
      : '-';
  const planAmount = isPaid ? formatSubscriptionAmount(subscription?.amount, subscription?.currency) : '-';
  const subscriptionStatus = subscription?.status || (chatAccess ? 'active' : accessStatus || 'inactive');
  const startDate = isPaid
    ? subscription?.start_at || effectiveFrom || trialStartedAt
    : trialStartedAt || effectiveFrom || subscription?.start_at;
  const endDate = isPaid
    ? subscription?.end_at || effectiveTo || trialEndsAt
    : trialEndsAt || effectiveTo || subscription?.end_at;

  let accountStateKey = 'no_subscription';
  if (accessStatus.includes('manual_review') || billingStatus === 'manual_review') {
    accountStateKey = 'manual_review';
  } else if (
    billingStatus === 'payment_failed' &&
    (trialStatus === 'expired' || trialStatus === 'access_removal_started')
  ) {
    accountStateKey = 'trial_payment_failed';
  } else if (billingStatus === 'payment_failed' && chatAccess) {
    accountStateKey = 'payment_failed_grace';
  } else if (
    accessStatus.includes('pending_deactivation') ||
    accessStatus.includes('deactivation_failed') ||
    billingStatus === 'payment_failed' ||
    entitlementReason.includes('grace_ended')
  ) {
    accountStateKey = 'payment_failed_ended';
  } else if (accessStatus.includes('pending_activation') || accessStatus.includes('payment_verified') || billingStatus === 'payment_verified') {
    accountStateKey = 'pending_activation';
  } else if (accessStatus.includes('activation_failed') || entitlementReason.includes('enqueue_failed')) {
    accountStateKey = 'activation_failed';
  } else if (trialStatus === 'trialing') {
    accountStateKey =
      trialDaysRemaining !== null && trialDaysRemaining <= 2 ? 'trial_ending_soon' : 'trial_active';
  } else if (trialStatus === 'expired' || trialStatus === 'access_removal_started') {
    accountStateKey = 'trial_expired_used';
  } else if (chatAccess) {
    accountStateKey = 'paid_active';
  } else if (accessStatus.includes('trial_already_used') || (trialStatus === 'converted_to_paid' && !chatAccess)) {
    accountStateKey = 'trial_expired_used';
  } else if (trialEligible || accessStatus === 'no_entitlement_record' || accessStatus === 'inactive') {
    accountStateKey = 'no_subscription';
  }

  const stateConfig = {
    no_subscription: {
      label: 'No Active Subscription',
      title: 'No active subscription',
      message: `Start your free trial to access ${CHAT_WORKSPACE_URL.replace(/^https?:\/\//, '')}.`,
      action: { type: 'link', label: 'Start Free Trial', to: PRICING_PATH },
    },
    trial_active: {
      label: 'Trial Active',
      title: 'Free trial active',
      message: hasPrepaidFutureSubscription
        ? `Free trial active until ${formatIstDate(trialEndsAt)}. You have already paid for the ${formatDisplayLabel(subscription?.billing_interval)} plan.`
        : trialDaysRemaining !== null
          ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining. Subscribe before trial end to continue access.`
          : 'Your free trial is active. Subscribe before it ends to continue access.',
      action: hasPrepaidFutureSubscription
        ? { type: 'external', label: 'Open Chat', href: CHAT_WORKSPACE_URL }
        : { type: 'link', label: 'Subscribe Now', to: PRICING_PATH },
    },
    trial_ending_soon: {
      label: 'Trial Ending Soon',
      title: 'Trial ending soon',
      message: hasPrepaidFutureSubscription
        ? `Free trial active until ${formatIstDate(trialEndsAt)}. You have already paid for the ${formatDisplayLabel(subscription?.billing_interval)} plan.`
        : trialDaysRemaining !== null
          ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining. Subscribe now to avoid interruption.`
          : 'Subscribe now to avoid interruption when trial ends.',
      action: hasPrepaidFutureSubscription
        ? { type: 'external', label: 'Open Chat', href: CHAT_WORKSPACE_URL }
        : { type: 'link', label: 'Subscribe Now', to: PRICING_PATH },
    },
    trial_expired_used: {
      label: 'Trial Ended',
      title: 'Trial ended',
      message: 'Free trial is no longer active for this account. Subscribe to restore access.',
      action: { type: 'link', label: 'Start Subscription', to: PRICING_PATH },
    },
    trial_payment_failed: {
      label: 'Trial Payment Failed',
      title: 'Trial payment failed',
      message: 'Your trial has ended and payment failed. Please subscribe again or update your payment method.',
      action: { type: 'link', label: 'Start Subscription', to: PRICING_PATH },
    },
    paid_active: {
      label: 'Subscription Active',
      title: 'Subscription active',
      message: 'Your subscription is active. You can continue using chat.',
      action: { type: 'external', label: 'Open Chat', href: CHAT_WORKSPACE_URL },
    },
    payment_failed_grace: {
      label: 'Payment Failed',
      title: 'Payment retrying',
      message: 'Renewal payment failed. Access remains active during grace. Retry payment now or update your saved card below.',
      action: { type: 'refresh', label: 'Refresh Status' },
    },
    payment_failed_ended: {
      label: 'Payment Failed',
      title: 'Access ending',
      message: 'Payment failed and access is ending or has ended. Update your card below or start a subscription from Pricing.',
      action: { type: 'link', label: 'View Pricing', to: PRICING_PATH },
    },
    pending_activation: {
      label: 'Pending Activation',
      title: 'Activating access',
      message: 'Payment or trial is confirmed and access activation is in progress.',
      action: { type: 'refresh', label: 'Refresh Status' },
    },
    manual_review: {
      label: 'Manual Review',
      title: 'Manual review',
      message: 'Your payment is being reviewed. Access will activate after verification.',
      action: { type: 'refresh', label: 'Refresh Status' },
    },
    activation_failed: {
      label: 'Activation Failed',
      title: 'Activation failed',
      message: 'Please retry from Pricing or contact support for assistance.',
      action: { type: 'link', label: 'View Pricing', to: PRICING_PATH },
    },
  };

  const selectedState = stateConfig[accountStateKey] || stateConfig.no_subscription;

  const periodLabels = {
    start: 'Effective from',
    end: 'Effective to',
  };

  if (accountStateKey === 'trial_active' || accountStateKey === 'trial_ending_soon') {
    periodLabels.start = 'Trial started';
    periodLabels.end = 'Trial ends';
  } else if (accountStateKey === 'paid_active') {
    periodLabels.start = 'Current period start';
    periodLabels.end = 'Current period end';
  } else if (hasSubscription) {
    periodLabels.start = 'Subscription start';
    periodLabels.end = 'Subscription end';
  }

  const periodStartValue =
    accountStateKey === 'trial_active' || accountStateKey === 'trial_ending_soon'
      ? formatIstDateTime(trialStartedAt || startDate)
      : formatIstDateTime(startDate);

  const periodEndValue =
    accountStateKey === 'trial_active' || accountStateKey === 'trial_ending_soon'
      ? formatIstDateTime(trialEndsAt || endDate)
      : formatIstDateTime(endDate);

  return {
    planName,
    subscriptionStatus,
    accessStatus,
    chatAccess,
    billingInterval,
    planAmount,
    startDate,
    endDate,
    trialStatus,
    trialEndsAt,
    trialDaysRemaining,
    isPaid,
    hasSubscription,
    accountStateKey,
    accountStateLabel: selectedState.label,
    accountStateTitle: selectedState.title,
    accountStateMessage: selectedState.message,
    primaryAccountAction: selectedState.action,
    periodLabels,
    periodStartValue,
    periodEndValue,
    scheduledForTrialEnd: Boolean(subscriptionPayload?.scheduled_for_trial_end),
    scheduledPlanCode: subscriptionPayload?.selected_plan_code || null,
    hasPrepaidFutureSubscription,
    trialEndChargeScheduled,
    lastChargeStatus,
    prepaidPlanCode,
    prepaidStartAt,
    prepaidEndAt,
    prepaidAutoRenew,
    canChangePlan: subscriptionPayload?.can_change_plan !== false,
    canCancelRenewal: Boolean(subscriptionPayload?.can_cancel_renewal),
  };
};

const formatScheduledPlanLabel = (planCode) => {
  if (planCode === 'CHAT_PRO_MONTHLY') return 'Monthly — Rs. 20,000 / month';
  if (planCode === 'CHAT_PRO_YEARLY') return 'Yearly — Rs. 220,000 / year';
  return planCode || 'Unknown plan';
};

const Account = () => {
  const { state, getAccessToken, getBasicUserInfo, getDecodedIDToken } = useAuthContext();
  const [searchParams] = useSearchParams();
  const paymentMethodSetupStatus = searchParams.get('payment_method_setup');

  const [userInfo, setUserInfo] = useState({
    username: '',
    givenname: '',
    email: '',
  });
  const [entitlementPayload, setEntitlementPayload] = useState(null);
  const [subscriptionPayload, setSubscriptionPayload] = useState(null);
  const [paymentMethodsPayload, setPaymentMethodsPayload] = useState({ payment_methods: [] });
  const [paymentMethodsError, setPaymentMethodsError] = useState(null);
  const [paymentMethodAction, setPaymentMethodAction] = useState(null);
  const [subscriptionAction, setSubscriptionAction] = useState(null);
  const [subscriptionActionError, setSubscriptionActionError] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const [showScheduledCancelConfirmation, setShowScheduledCancelConfirmation] = useState(false);

  const [paymentMethodConfirmConfig, setPaymentMethodConfirmConfig] = useState(null);
  const [preapprovalNotice, setPreapprovalNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runWithTimeout = useCallback((promise, timeoutMs = 8000) => {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        reject(new Error('Request timeout'));
      }, timeoutMs);

      promise
        .then((value) => {
          window.clearTimeout(timer);
          resolve(value);
        })
        .catch((requestError) => {
          window.clearTimeout(timer);
          reject(requestError);
        });
    });
  }, []);

  const loadAccountData = useCallback(async () => {
    if (!state?.isAuthenticated) {
      setLoading(false);
      setEntitlementPayload(null);
      setSubscriptionPayload(null);
      setPaymentMethodsPayload({ payment_methods: [] });
      setPaymentMethodsError(null);
      setSubscriptionActionError(null);
      setShowCancelConfirmation(false);
      return;
    }

    setLoading(true);
    setError(null);
    setPaymentMethodsError(null);
    setSubscriptionActionError(null);

    if (paymentMethodSetupStatus === 'pending') {
      setPreapprovalNotice(PAYMENT_METHOD_RETURN_MESSAGE);
    } else if (paymentMethodSetupStatus === 'cancelled') {
      setPreapprovalNotice('Payment method setup was cancelled. No card was saved unless PayHere confirms setup separately.');
    }

    try {
      const [info, decodedToken] = await Promise.all([
        getBasicUserInfo().catch(() => ({})),
        getDecodedIDToken().catch(() => ({})),
      ]);
      const email = info?.email || decodedToken?.email || '';
      const username = info?.displayName || info?.givenName || '';
      const givenname = info?.givenName || info?.displayName || '';

      setUserInfo({ username, givenname, email });

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Authentication token is missing.');
      }

      const buildAuthenticatedGet = (path) =>
        runWithTimeout(
          fetch(`${CHAT_DB_SERVER_URL}${path}`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          8000
        );

      const [entitlementResult, subscriptionResult, paymentMethodsResult] = await Promise.allSettled([
        buildAuthenticatedGet('/chat-entitlements/me'),
        buildAuthenticatedGet('/chat-subscriptions/me'),
        buildAuthenticatedGet('/payment-methods'),
      ]);

      const parseResult = async (result, label) => {
        if (result.status !== 'fulfilled') {
          return { ok: false, payload: null, message: result.reason?.message || `${label} request failed` };
        }
        const response = result.value;
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          return {
            ok: false,
            payload: null,
            message: payload?.detail || payload?.error || `${label} HTTP ${response.status}`,
          };
        }
        return { ok: true, payload, message: '' };
      };

      const [entitlementParsed, subscriptionParsed, paymentMethodsParsed] = await Promise.all([
        parseResult(entitlementResult, 'Entitlement'),
        parseResult(subscriptionResult, 'Subscription'),
        parseResult(paymentMethodsResult, 'Payment methods'),
      ]);

      setEntitlementPayload(entitlementParsed.payload);
      setSubscriptionPayload(subscriptionParsed.payload);
      setPaymentMethodsPayload(
        paymentMethodsParsed.ok && paymentMethodsParsed.payload
          ? paymentMethodsParsed.payload
          : { payment_methods: [] }
      );
      setPaymentMethodsError(paymentMethodsParsed.ok ? null : paymentMethodsParsed.message);

      if (!entitlementParsed.ok && !subscriptionParsed.ok) {
        throw new Error(
          entitlementParsed.message || subscriptionParsed.message || 'Unable to load access details.'
        );
      }
    } catch (fetchError) {
      setEntitlementPayload(null);
      setSubscriptionPayload(null);
      setPaymentMethodsPayload({ payment_methods: [] });
      setPaymentMethodsError(null);
      setSubscriptionActionError(null);
      setError(fetchError?.message || 'Unable to load access details. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [
    getAccessToken,
    getBasicUserInfo,
    getDecodedIDToken,
    runWithTimeout,
    paymentMethodSetupStatus,
    state?.isAuthenticated,
  ]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  const accountModel = useMemo(
    () => deriveAccountModel(entitlementPayload, subscriptionPayload),
    [entitlementPayload, subscriptionPayload]
  );

  const paymentMethods = useMemo(
    () => (Array.isArray(paymentMethodsPayload?.payment_methods) ? paymentMethodsPayload.payment_methods : []),
    [paymentMethodsPayload]
  );
  const activePaymentMethods = useMemo(
    () => paymentMethods.filter((method) => method.status === 'active'),
    [paymentMethods]
  );
  const primaryPaymentMethod = activePaymentMethods.find((method) => method.is_primary) || null;
  const backupPaymentMethod = activePaymentMethods.find((method) => method.is_backup) || null;
  const unassignedPaymentMethods = activePaymentMethods.filter(
    (method) => !method.is_primary && !method.is_backup
  );
  const currentSubscription = subscriptionPayload?.subscription || null;
  const cancellationMode = String(currentSubscription?.cancellation_mode || 'none').trim().toLowerCase();
  const isActivePaidSubscription = Boolean(
    currentSubscription?.is_paid && String(currentSubscription?.status || '').trim().toLowerCase() === 'active'
  );
  const canCancelSubscription = (isActivePaidSubscription || accountModel.canCancelRenewal) && cancellationMode === 'none';

  const canResumeSubscription = isActivePaidSubscription && cancellationMode === 'cancel_at_period_end';
  const canRetryGracePayment = accountModel.accountStateKey === 'payment_failed_grace';
  const canCancelScheduledTrialPlan =
    accountModel.trialStatus === 'trialing' &&
    Boolean(subscriptionPayload?.can_cancel_scheduled_payment || subscriptionPayload?.scheduled_for_trial_end) &&
    !accountModel.hasPrepaidFutureSubscription;
  const subscriptionPeriodEndLabel = formatIstDateTime(currentSubscription?.end_at);
  const cancelEffectiveLabel = formatIstDateTime(
    currentSubscription?.cancel_effective_at || currentSubscription?.end_at
  );

  const showPaymentMethods =
    !loading && (
      accountModel.isPaid ||
      accountModel.scheduledForTrialEnd ||
      activePaymentMethods.length > 0
    );


  const renderPrimaryAction = (action) => {
    if (!action) {
      return null;
    }

    if (action.type === 'external') {
      return (
        <a
          href={action.href}
          className="inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover sm:w-auto"
        >
          {action.label}
        </a>
      );
    }

    if (action.type === 'link') {
      return (
        <Link
          to={action.to}
          className="inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover sm:w-auto"
        >
          {action.label}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={loadAccountData}
        className="inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover sm:w-auto"
      >
        {action.label}
      </button>
    );
  };

  const buildPaymentMethodReturnUrl = (status) => {
    const url = new URL(window.location.href);
    url.searchParams.set('payment_method_setup', status);
    return url.toString();
  };

  const authenticatedPaymentMethodRequest = async (path, options = {}) => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('Authentication token is missing.');
    }

    const response = await fetch(`${CHAT_DB_SERVER_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.detail || payload?.error || `HTTP ${response.status}`);
    }
    return payload;
  };

  const handleRetryGracePayment = async () => {
    if (!state?.isAuthenticated || subscriptionAction) {
      return;
    }

    setSubscriptionAction('retry-payment');
    setSubscriptionActionError(null);
    try {
      const payload = await authenticatedPaymentMethodRequest('/chat-subscriptions/saved-card/start', {
        method: 'POST',
        body: JSON.stringify({
          plan_code: currentSubscription?.plan_code || 'CHAT_PRO_MONTHLY',
          intent: 'paid_grace_retry',
        }),
      });
      const retryStatus = String(payload?.status || '').trim();
      if (retryStatus === 'grace_retry_success') {
        await loadAccountData();
        return;
      }
      if (retryStatus === 'grace_retry_pending' || retryStatus === 'grace_retry_blocked_in_progress') {
        setSubscriptionActionError(payload?.message || 'Payment retry is already being processed. Refresh status before trying again.');
        await loadAccountData();
        return;
      }
      setSubscriptionActionError(payload?.message || 'Payment retry did not complete. Grace remains active until the deadline.');
      await loadAccountData();
    } catch (retryError) {
      setSubscriptionActionError(retryError?.message || 'Unable to retry payment.');
    } finally {
      setSubscriptionAction(null);
    }
  };

  const handleConfirmCancelSubscription = async () => {
    if (!state?.isAuthenticated || subscriptionAction) {
      return;
    }

    setSubscriptionAction('cancel');
    setSubscriptionActionError(null);
    try {
      await authenticatedPaymentMethodRequest('/chat-subscriptions/cancel-at-period-end', {
        method: 'POST',
      });
      setShowCancelConfirmation(false);
      await loadAccountData();
    } catch (cancelError) {
      setSubscriptionActionError(cancelError?.message || 'Unable to cancel subscription.');
    } finally {
      setSubscriptionAction(null);
    }
  };

  const handleCancelScheduledTrialPlan = async () => {
    if (!state?.isAuthenticated || subscriptionAction) {
      return;
    }
    setSubscriptionAction('cancel-scheduled');
    setSubscriptionActionError(null);
    try {
      await authenticatedPaymentMethodRequest('/chat-subscriptions/cancel-trial-selection', {
        method: 'POST',
      });

      setShowScheduledCancelConfirmation(false);

      await loadAccountData();
    } catch (cancelError) {
      setSubscriptionActionError(cancelError?.message || 'Unable to cancel scheduled payment.');
    } finally {
      setSubscriptionAction(null);
    }
  };

  const handleResumeSubscription = async () => {
    if (!state?.isAuthenticated || subscriptionAction) {
      return;
    }

    setSubscriptionAction('resume');
    setSubscriptionActionError(null);
    try {
      await authenticatedPaymentMethodRequest('/chat-subscriptions/resume', {
        method: 'POST',
      });
      await loadAccountData();
    } catch (resumeError) {
      setSubscriptionActionError(resumeError?.message || 'Unable to resume subscription.');
    } finally {
      setSubscriptionAction(null);
    }
  };

  const handleStartPreapproval = async (cardRole) => {
    if (!state?.isAuthenticated || paymentMethodAction) {
      return;
    }

    const normalizedRole = cardRole === 'backup' ? 'backup' : 'primary';
    setPaymentMethodAction(`add-${normalizedRole}`);
    setPaymentMethodsError(null);
    setPreapprovalNotice('Redirecting to PayHere to set up your payment method.');

    try {
      const payload = await authenticatedPaymentMethodRequest('/payment-methods/preapproval/start', {
        method: 'POST',
        body: JSON.stringify({
          purpose: normalizedRole === 'primary' ? 'add_primary_card' : 'add_backup_card',
          card_role: normalizedRole,
          return_url: buildPaymentMethodReturnUrl('pending'),
          cancel_url: buildPaymentMethodReturnUrl('cancelled'),
        }),
      });

      if (!payload?.action_url || !payload?.form_payload) {
        throw new Error('Payment method setup did not return a PayHere form payload.');
      }

      submitPayHerePreapprovalForm({
        actionUrl: payload.action_url,
        formPayload: payload.form_payload,
        method: payload.method,
      });
    } catch (setupError) {
      setPaymentMethodAction(null);
      setPreapprovalNotice('');
      setPaymentMethodsError(formatPaymentMethodError(setupError?.message || 'Unable to start payment method setup.'));
    }
  };

  const buildPaymentMethodConfirmation = (method, action) => {
    if (!method) {
      return '';
    }
    if (action === 'make-primary' && method.is_backup) {
      return 'This Secondary card will become Primary and your current Primary card will become Secondary.';
    }
    if (action === 'make-backup' && method.is_primary) {
      return 'This Primary card will become Secondary and your current Secondary card will become Primary.';
    }
    if (action === 'disable') {
      if (method.is_primary && backupPaymentMethod && backupPaymentMethod.id !== method.id) {
        return 'Your Secondary card will become the new Primary payment method.';
      }
      return 'Remove this saved card?';
    }
    return '';
  };

  const executePaymentMethodAction = async (method, action) => {
    const paymentMethodId = method?.id;
    setPaymentMethodAction(`${action}-${paymentMethodId}`);
    setPaymentMethodsError(null);
    try {
      const payload = await authenticatedPaymentMethodRequest(`/payment-methods/${paymentMethodId}/${action}`, {
        method: 'POST',
      });
      setPaymentMethodsPayload(payload);
      setPreapprovalNotice(action === 'disable' ? 'Payment method removed.' : 'Payment method updated.');
    } catch (updateError) {
      setPaymentMethodsError(formatPaymentMethodError(updateError?.message || 'Unable to update payment method.'));
    } finally {
      setPaymentMethodAction(null);
    }
  };

  const handlePaymentMethodRoleAction = (method, action) => {
    const paymentMethodId = method?.id;
    if (!paymentMethodId || paymentMethodAction) {
      return;
    }
    const confirmation = buildPaymentMethodConfirmation(method, action);
    if (confirmation) {
      setPaymentMethodConfirmConfig({
        method,
        action,
        message: confirmation,
        title: action === 'disable' ? 'Remove card' : 'Update payment method',
      });
      return;
    }
    executePaymentMethodAction(method, action);
  };


  const renderPaymentMethodCard = (method, title) => {

    if (!method) {
      if (loading) {
        return (
          <div className="animate-pulse rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-4">
            <div className="h-3 w-20 rounded bg-gray-200" />
            <div className="mt-3 h-4 w-36 rounded bg-gray-200" />
            <div className="mt-2 h-3 w-24 rounded bg-gray-200" />
          </div>
        );
      }
      const cardRole = title === 'Secondary Card' ? 'backup' : 'primary';
      return (
        <div className="rounded-pl border border-dashed border-pl-border bg-pl-bg-subtle px-4 py-4">
          <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{title}</p>
          <p className="mt-2 text-14 text-pl-text-secondary">No card set.</p>
          <button
            type="button"
            disabled={Boolean(paymentMethodAction) || (cardRole === 'backup' && !primaryPaymentMethod)}
            title={cardRole === 'backup' && !primaryPaymentMethod ? 'Please add a Primary Card first' : ''}
            onClick={() => handleStartPreapproval(cardRole)}
            className="mt-3 inline-flex items-center justify-center rounded-pl border border-pl-border px-3 py-2 text-13 font-semibold text-pl-text-primary transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {paymentMethodAction === `add-${cardRole}` ? 'Redirecting...' : `Add ${title}`}
          </button>
        </div>
      );
    }

    const isActive = method.status === 'active';
    const actionDisabled = Boolean(paymentMethodAction) || !isActive;

    return (
      <div className="rounded-pl border border-pl-border bg-white px-4 py-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{title}</p>
            <p className="mt-2 text-15 font-semibold text-pl-text-primary">{formatCardLabel(method)}</p>
            <p className="mt-1 text-13 text-pl-text-secondary">
              {method.card_expiry ? `Expires ${method.card_expiry}` : 'Expiry unavailable'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-12">
            <span className="rounded-full border border-pl-border px-3 py-1 text-pl-text-secondary">
              {resolvePaymentMethodRole(method)}
            </span>
            <span className="rounded-full border border-pl-border px-3 py-1 text-pl-text-secondary">
              {formatCardStatus(method.status)}
            </span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {!method.is_primary && isActive && (
            <button
              type="button"
              disabled={actionDisabled}
              onClick={() => handlePaymentMethodRoleAction(method, 'make-primary')}
              className="inline-flex items-center justify-center rounded-pl border border-pl-border px-3 py-2 text-13 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle disabled:cursor-not-allowed disabled:opacity-60"
            >
              Make Primary
            </button>
          )}
          {!method.is_primary && !method.is_backup && isActive && (
            <button
              type="button"
              disabled={actionDisabled}
              onClick={() => handlePaymentMethodRoleAction(method, 'make-backup')}
              className="inline-flex items-center justify-center rounded-pl border border-pl-border px-3 py-2 text-13 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle disabled:cursor-not-allowed disabled:opacity-60"
            >
              Make Secondary
            </button>
          )}
          {isActive && (
            <button
              type="button"
              disabled={Boolean(paymentMethodAction)}
              onClick={() => handlePaymentMethodRoleAction(method, 'disable')}
              className="inline-flex items-center justify-center rounded-pl border border-pl-warning px-3 py-2 text-13 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              Remove card
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <Helmet>
        <title>My Account | paralegal.lk</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {showCancelConfirmation && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-md rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-md">
            <p className="text-12 font-semibold uppercase tracking-[0.16em] text-pl-accent">Cancel Renewal</p>
            <h2 className="mt-2 text-22 font-display font-semibold text-pl-text-primary">Confirm cancellation</h2>
            <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
              Your renewal will be cancelled. Your already-paid period remains active until {subscriptionPeriodEndLabel}.
            </p>
            {subscriptionActionError && (
              <div className="mt-4 rounded-pl border border-pl-warning/30 bg-[rgba(188,108,37,0.08)] px-4 py-3 text-14 text-pl-text-primary">
                {subscriptionActionError}
              </div>
            )}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={Boolean(subscriptionAction)}
                onClick={() => {
                  setSubscriptionActionError(null);
                  setShowCancelConfirmation(false);
                }}
                className="inline-flex items-center justify-center rounded-pl border border-pl-border px-5 py-2.5 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle disabled:cursor-not-allowed disabled:opacity-60"
              >
                Keep Subscription
              </button>
              <button
                type="button"
                disabled={Boolean(subscriptionAction)}
                onClick={handleConfirmCancelSubscription}
                className="inline-flex items-center justify-center rounded-pl border border-pl-warning px-5 py-2.5 text-14 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {subscriptionAction === 'cancel' ? 'Cancelling...' : 'Confirm Renewal Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduledCancelConfirmation && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-md rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-md">
            <p className="text-12 font-semibold uppercase tracking-[0.16em] text-pl-accent">Cancel Scheduled Payment</p>
            <h2 className="mt-2 text-22 font-display font-semibold text-pl-text-primary">Confirm cancellation</h2>
            <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
              Are you sure you want to cancel your scheduled payment? Your access will end when your trial expires unless you subscribe again.
            </p>
            {subscriptionActionError && (
              <div className="mt-4 rounded-pl border border-pl-warning/30 bg-[rgba(188,108,37,0.08)] px-4 py-3 text-14 text-pl-text-primary">
                {subscriptionActionError}
              </div>
            )}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={Boolean(subscriptionAction)}
                onClick={() => {
                  setSubscriptionActionError(null);
                  setShowScheduledCancelConfirmation(false);
                }}
                className="inline-flex items-center justify-center rounded-pl border border-pl-border px-5 py-2.5 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle disabled:cursor-not-allowed disabled:opacity-60"
              >
                Keep Scheduled Payment
              </button>
              <button
                type="button"
                disabled={Boolean(subscriptionAction)}
                onClick={handleCancelScheduledTrialPlan}
                className="inline-flex items-center justify-center rounded-pl border border-pl-warning px-5 py-2.5 text-14 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {subscriptionAction === 'cancel-scheduled' ? 'Cancelling...' : 'Cancel Scheduled Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 md:p-8 lg:p-12 mt-30 lg:mt-10 mb-[-25px]">
        <div className="mx-auto max-w-7xl space-y-8">
          <section className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
            <h1 className="text-3xl font-display font-semibold text-pl-text-primary md:text-4xl">
              Welcome,{' '}
              {loading ? (
                <span className="inline-block h-8 w-32 animate-pulse rounded bg-gray-200" />
              ) : (
                <span className="text-pl-accent">{userInfo.givenname || 'there'}!</span>
              )}
            </h1>
            <p className="mt-2 text-16 text-pl-text-secondary">
              Manage your account settings and subscription access from one place.
            </p>
          </section>

          {error && (
            <section className="rounded-[16px] border border-pl-warning/30 bg-[rgba(188,108,37,0.08)] px-5 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-14 text-pl-text-primary">{error}</p>
                <button
                  type="button"
                  onClick={loadAccountData}
                  className="inline-flex items-center justify-center rounded-pl border border-pl-warning px-4 py-2 text-13 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white"
                >
                  Retry
                </button>
              </div>
            </section>
          )}

          <section className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <article className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
              <h2 className="text-22 font-display font-semibold text-pl-text-primary">Personal Details</h2>
              <div className="mt-6 space-y-4 text-14">
                <div className="flex items-center justify-between gap-4 border-b border-pl-border pb-3">
                  <span className="text-pl-text-tertiary">Name</span>
                  <span className="font-medium text-pl-text-primary">{loading ? 'Loading...' : userInfo.username || '-'}</span>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-pl-border pb-3">
                  <span className="text-pl-text-tertiary">Email</span>
                  <span className="font-medium text-pl-text-primary">{loading ? 'Loading...' : userInfo.email || '-'}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  window.location.href = `https://accounts.asgardeo.io/t/paralegallk/accountrecoveryendpoint/recoveraccountrouter.do?client_id=${import.meta.env.VITE_ASGARDEO_CLIENT_ID}&redirect_uri=${import.meta.env.VITE_SIGN_IN_URL}`;
                }}
                className="mt-8 inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
              >
                Reset Password
              </button>
            </article>

            <article className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-12 font-semibold uppercase tracking-[0.16em] text-pl-accent">Subscription Center</p>
                  <h2 className="mt-2 text-22 font-display font-semibold text-pl-text-primary">Subscription & Access</h2>
                </div>
                <button
                  type="button"
                  onClick={loadAccountData}
                  className="inline-flex items-center justify-center rounded-pl border border-pl-border px-4 py-2 text-13 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                >
                  Refresh Status
                </button>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Plan</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : accountModel.planName}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Status</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : accountModel.accountStateLabel}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Billing Cycle</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : formatDisplayLabel(accountModel.billingInterval)}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">Price</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">{loading ? 'Loading...' : accountModel.planAmount}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-pl border border-pl-border bg-white px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{accountModel.periodLabels.start}</p>
                  <p className="mt-1 text-14 font-medium text-pl-text-primary">{loading ? 'Loading...' : accountModel.periodStartValue}</p>
                </div>
                <div className="rounded-pl border border-pl-border bg-white px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{accountModel.periodLabels.end}</p>
                  <p className="mt-1 text-14 font-medium text-pl-text-primary">{loading ? 'Loading...' : accountModel.periodEndValue}</p>
                </div>
              </div>

              {!loading && accountModel.scheduledForTrialEnd && accountModel.scheduledPlanCode && accountModel.accountStateLabel !== 'Subscription Active' && (
                <div className="mt-4 rounded-pl border border-pl-border bg-white px-4 py-3">
                  <p className="text-12 uppercase tracking-[0.12em] text-pl-text-tertiary">{accountModel.hasPrepaidFutureSubscription ? 'Paid Scheduled Plan' : 'Scheduled Payment'}</p>
                  <p className="mt-1 text-14 font-semibold text-pl-text-primary">
                    {formatScheduledPlanLabel(accountModel.scheduledPlanCode)}
                  </p>
                  <p className="mt-1 text-13 text-pl-text-secondary">
                    {accountModel.hasPrepaidFutureSubscription
                      ? `You have already paid. Paid subscription starts ${formatIstDate(accountModel.prepaidStartAt || accountModel.trialEndsAt)} and ${accountModel.prepaidAutoRenew === false ? 'ends' : 'renews'} ${formatIstDate(accountModel.prepaidEndAt)}.`
                      : accountModel.lastChargeStatus && ['failed', 'cancelled', 'error'].includes(accountModel.lastChargeStatus)
                        ? `Your card was saved, but payment failed. We will try to charge this plan again when your free trial ends on ${formatIstDate(accountModel.trialEndsAt)}.`
                        : `Scheduled to charge when your free trial ends on ${formatIstDate(accountModel.trialEndsAt)}.`}
                  </p>
                  {accountModel.hasPrepaidFutureSubscription && (
                    <p className="mt-1 text-13 text-pl-text-secondary">
                      Renewal: {accountModel.prepaidAutoRenew === false ? 'Cancelled' : 'Active'}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-6 rounded-[16px] border border-pl-border bg-pl-bg-elevated px-4 py-4">
                <h3 className="mt-2 text-16 font-semibold text-pl-text-primary">{loading ? 'Checking account state...' : accountModel.accountStateTitle}</h3>

                <p className="mt-2 text-14 leading-7 text-pl-text-secondary">
                  {loading ? 'Loading the latest subscription and access details.' : accountModel.accountStateMessage}
                </p>

                {!loading && accountModel.accountStateLabel === 'Pending Activation' && (
                  <div className="mt-4 flex items-center gap-3">
                    <svg
                      className="h-5 w-5 animate-spin text-pl-accent"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    <span className="text-13 text-pl-text-secondary">Activating your access, this may take a few moments...</span>
                  </div>
                )}

                {!loading && accountModel.trialEndsAt && accountModel.trialStatus === 'trialing' && (
                  <p className="mt-2 text-13 text-pl-text-tertiary">
                    Trial end date: {formatIstDate(accountModel.trialEndsAt)}
                  </p>
                )}

                {!loading && canResumeSubscription && (
                  <p className="mt-3 text-13 font-medium text-pl-warning">
                    Subscription will end on {cancelEffectiveLabel}
                  </p>
                )}

                {subscriptionActionError && (
                  <div className="mt-4 rounded-pl border border-pl-warning/30 bg-[rgba(188,108,37,0.08)] px-4 py-3 text-14 text-pl-text-primary">
                    {subscriptionActionError}
                  </div>
                )}

                {!loading && (
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                    {renderPrimaryAction(accountModel.primaryAccountAction)}

                    <Link
                      to={PRICING_PATH}
                      className="inline-flex w-full items-center justify-center rounded-pl border border-pl-border px-5 py-2.5 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle sm:w-auto"
                    >
                      View Pricing
                    </Link>

                    {(canRetryGracePayment || canCancelSubscription || canResumeSubscription || canCancelScheduledTrialPlan ||
                      accountModel.accountStateLabel === 'Activation Failed' || accountModel.accountStateLabel === 'Manual Review') && (
                        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:border-l sm:border-pl-border sm:pl-4">
                          {canRetryGracePayment && (
                            <>
                              <button
                                type="button"
                                disabled={Boolean(subscriptionAction)}
                                onClick={handleRetryGracePayment}
                                className="inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                              >
                                {subscriptionAction === 'retry-payment' ? 'Retrying...' : 'Retry Payment Now'}
                              </button>
                            </>
                          )}
                          {canCancelSubscription && (
                            <button
                              type="button"
                              disabled={Boolean(subscriptionAction)}
                              onClick={() => {
                                setSubscriptionActionError(null);
                                setShowCancelConfirmation(true);
                              }}
                              className="inline-flex w-full items-center justify-center rounded-pl border border-pl-warning px-5 py-2.5 text-14 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                              Cancel Renewal
                            </button>
                          )}
                          {canResumeSubscription && (
                            <button
                              type="button"
                              disabled={Boolean(subscriptionAction)}
                              onClick={handleResumeSubscription}
                              className="inline-flex w-full items-center justify-center rounded-pl bg-pl-accent px-5 py-2.5 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                              {subscriptionAction === 'resume' ? 'Resuming...' : 'Resume Subscription'}
                            </button>
                          )}
                          {canCancelScheduledTrialPlan && (
                            <button
                              type="button"
                              disabled={Boolean(subscriptionAction)}
                              onClick={() => {
                                setSubscriptionActionError(null);
                                setShowScheduledCancelConfirmation(true);
                              }}

                              className="inline-flex w-full items-center justify-center rounded-pl border border-pl-warning px-5 py-2.5 text-14 font-semibold text-pl-warning transition-colors hover:bg-pl-warning hover:text-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                              {subscriptionAction === 'cancel-scheduled' ? 'Cancelling...' : 'Cancel Scheduled Payment'}
                            </button>
                          )}
                          {(accountModel.accountStateLabel === 'Activation Failed' || accountModel.accountStateLabel === 'Manual Review') && (
                            <a
                              href="mailto:admin@paralegal.lk?subject=Chat%20Subscription%20Support"
                              className="inline-flex w-full items-center justify-center rounded-pl border border-pl-border px-5 py-2.5 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle sm:w-auto"
                            >
                              Contact Support
                            </a>
                          )}
                        </div>
                      )}
                  </div>
                )}

              </div>
            </article>
          </section>
          {showPaymentMethods && (
            <section className="rounded-[20px] border border-pl-border bg-white p-6 shadow-pl-sm md:p-8">
              <div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-12 font-semibold uppercase tracking-[0.16em] text-pl-accent">Billing Setup</p>
                    <h2 className="mt-2 text-22 font-display font-semibold text-pl-text-primary">Payment Methods</h2>
                  </div>
                </div>
                <p className="mt-2 text-14 leading-7 text-pl-text-secondary">
                  Cards are saved for automated billing. Trial plan selection charges the selected plan today and schedules paid access after trial. PayHere may use a refundable LKR 10 authorization to verify the card.
                </p>
              </div>


              {preapprovalNotice && (
                <div className="mt-5 rounded-pl border border-pl-border bg-pl-bg-subtle px-4 py-3 text-14 text-pl-text-secondary">
                  {preapprovalNotice}
                </div>
              )}

              {paymentMethodsError && (
                <div className="mt-5 rounded-pl border border-pl-warning/30 bg-[rgba(188,108,37,0.08)] px-4 py-3 text-14 text-pl-text-primary">
                  {paymentMethodsError}
                </div>
              )}

              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {renderPaymentMethodCard(primaryPaymentMethod, 'Primary Card')}
                {renderPaymentMethodCard(backupPaymentMethod, 'Secondary Card')}
              </div>

              {unassignedPaymentMethods.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-16 font-semibold text-pl-text-primary">Saved cards requiring action</h3>
                  <div className="mt-3 grid gap-4 lg:grid-cols-2">
                    {unassignedPaymentMethods.map((method) => (
                      <React.Fragment key={method.id}>
                        {renderPaymentMethodCard(method, 'Saved Card')}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

            </section>
          )}

        </div>
      </div>
      <DialogModal
        isOpen={Boolean(paymentMethodConfirmConfig)}
        onClose={() => setPaymentMethodConfirmConfig(null)}
        title={paymentMethodConfirmConfig?.title || 'Confirm'}
        description={paymentMethodConfirmConfig?.message || ''}
        actions={[
          {
            label: 'Cancel',
            onClick: () => setPaymentMethodConfirmConfig(null),
          },
          {
            label: paymentMethodConfirmConfig?.action === 'disable' ? 'Remove' : 'Confirm',
            variant: 'primary',
            onClick: () => {
              const config = paymentMethodConfirmConfig;
              setPaymentMethodConfirmConfig(null);
              executePaymentMethodAction(config.method, config.action);
            },
          },
        ]}
      />
    </MainLayout>
  );
};

export default Account;
